import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./PrivateCustomers.module.css";
import AddPrivateCustomerModal from "../../../../components/Customers/AddPrivateCustomerModal";
import {
  usePrivateCustomersList,
  useCreatePrivateCustomer,
} from "../../../../api/people/usePeopleApi";

// ── Helpers: API response → UI format ─────────────────────────────────────
const mapApiToUi = (c) => ({
  id: c.id,
  ref: c.customerCode || `CUST-PRV-${String(c.id).padStart(5, "0")}`,
  createdAt: c.createdAt
    ? new Date(c.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
    : "",
  firstName: c.firstName ?? "",
  lastName: c.lastName ?? "",
  email: c.email ?? "",
  phone: c.phone ?? "",
  nationality: c.nationality ?? "",
  bookingsCount: c.totalBookings ?? 0,
  totalSpent: parseFloat(c.totalSpent ?? 0),
  openBalance: parseFloat(c.openBalance ?? 0),
  status: c.status === "ACTIVE" ? "Active" : "Inactive",
});

// ── Constants ──────────────────────────────────────────────────────────────
const STATUS_TABS = [
  { key: "All",      label: "All Customers" },
  { key: "Active",   label: "Active" },
  { key: "Inactive", label: "Inactive" },
];

// ── Helpers ────────────────────────────────────────────────────────────────
const initials    = (c) => `${c.firstName[0]}${c.lastName[0]}`.toUpperCase();
const avatarColor = (id) => {
  const colors = ["#f5a51e","#059669","#2563eb","#9333ea","#dc2626","#0891b2","#d97706","#16a34a"];
  return colors[id % colors.length];
};

// ── Component ──────────────────────────────────────────────────────────────
const PrivateCustomers = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // List filters
  const [activeTab,         setActiveTab]         = useState("All");
  const [searchTerm,        setSearchTerm]        = useState("");
  const [balanceFilter,     setBalanceFilter]     = useState("all");
  const [nationalityFilter, setNationalityFilter] = useState("all");

  // Modal
  const [modalOpen,       setModalOpen]       = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null); // null = add mode

  // ── Pagination ──
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // ── API ──
  const { data: apiData, pagination, loading, error, refetch } = usePrivateCustomersList({
    q: searchTerm || undefined,
    status: activeTab,
    balance: balanceFilter,
    page,
    limit,
  });
  const { execute: createCustomer, loading: saving, error: createError, clearError } = useCreatePrivateCustomer();

  const customers = useMemo(() => (apiData ?? []).map(mapApiToUi), [apiData]);

  // ── Navigation ──
  const handleOpenCustomer = (customer) =>
    navigate(`/people/customers/private/${customer.ref}`, { state: { customer } });

  const handleOpenBookings = (customer) =>
    navigate(`/people/customers/private/${customer.ref}`, { state: { customer, defaultTab: "bookings" } });

  // ── Modal ──
  const handleAddClick = () => {
    setEditingCustomer(null);
    clearError?.();
    setModalOpen(true);
  };

  // Auto-open the "Add Client" form when navigated here from the booking
  // "Change Customer" flow (Add New Client button).
  useEffect(() => {
    if (location.state?.openAdd) {
      setEditingCustomer(null);
      clearError?.();
      setModalOpen(true);
      // Clear nav state so the modal doesn't reopen on refresh/back.
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.openAdd]);

  const handleSave = useCallback(
    async (payload) => {
      try {
        await createCustomer(payload);
        setModalOpen(false);
        refetch();
      } catch (err) {
        // Error already set in hook; modal stays open for retry
        console.error("Save customer failed:", err);
      }
    },
    [createCustomer, refetch]
  );

  // ── Derived data (client-side filtering for nationality - API doesn't support it) ──
  const nationalities = useMemo(() => {
    return Array.from(new Set(customers.map((c) => c.nationality))).filter(Boolean).sort();
  }, [customers]);

  const tabCounts = useMemo(() => {
    // Only show count for the active tab (from API); others show "—"
    const total = pagination?.total ?? 0;
    const counts = { All: "—", Active: "—", Inactive: "—" };
    counts[activeTab] = total;
    return counts;
  }, [pagination?.total, activeTab]);

  const hasActiveFilters = searchTerm !== "" || balanceFilter !== "all" || nationalityFilter !== "all";

  const filtered = useMemo(
    () =>
      customers.filter((c) => {
        const matchesTab = activeTab === "All" || c.status === activeTab;
        const matchesSearch =
          c.ref.toLowerCase().includes(searchTerm.toLowerCase()) ||
          `${c.firstName} ${c.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesBalance =
          balanceFilter === "all" ||
          (balanceFilter === "owed" && c.openBalance > 0) ||
          (balanceFilter === "clear" && c.openBalance === 0);
        const matchesNationality = nationalityFilter === "all" || c.nationality === nationalityFilter;
        return matchesTab && matchesSearch && matchesBalance && matchesNationality;
      }),
    [customers, activeTab, searchTerm, balanceFilter, nationalityFilter]
  );

  const handleClear = () => {
    setSearchTerm("");
    setBalanceFilter("all");
    setNationalityFilter("all");
    setPage(1);
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, balanceFilter, activeTab]);

  // Page numbers for pagination (1, 2, 3, …): like GeoData
  const pageNumbers = useMemo(() => {
    const total = pagination?.totalPages ?? 1;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const current = page;
    const pages = new Set([1, total]);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) pages.add(i);
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    let prev = 0;
    for (const p of sorted) {
      if (p > prev + 1) result.push("…");
      result.push(p);
      prev = p;
    }
    return result;
  }, [pagination?.totalPages, page]);

  return (
    <div className={styles.container}>

      {/* ── Error Banner ── */}
      {error && (
        <div className={styles.errorBanner}>
          {error}
          <button className={styles.retryBtn} onClick={() => refetch()}>Retry</button>
        </div>
      )}

      {/* ── Page Header ── */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>👤</div>
          <div className={styles.headerText}>
            <h1>Private Customers</h1>
            <p>Individual customers — legal and financial counterparty for all bookings</p>
          </div>
        </div>
        <button className={styles.addBtn} onClick={handleAddClick}>+ Add Customer</button>
      </div>

      {/* ── Status Tabs ── */}
      <div className={styles.tabBar}>
        {STATUS_TABS.map(tab => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              className={`${styles.tab} ${isActive ? styles.tabActive : ""} ${
                isActive && tab.key === "Active"   ? styles.tabActiveGreen : ""} ${
                isActive && tab.key === "Inactive" ? styles.tabActiveGray  : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              <span className={`${styles.tabBadge} ${isActive ? styles.tabBadgeActive : ""} ${
                !isActive && tab.key === "Active"   ? styles.tabBadgeGreen : ""} ${
                !isActive && tab.key === "Inactive" ? styles.tabBadgeGray  : ""}`}>
                {tabCounts[tab.key] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Filter Bar ── */}
      <div className={styles.filterBar}>
        <div className={styles.searchWrap}>
          <span className={styles.searchIcon}>🔍</span>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by ref, name, or email..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className={styles.filterDivider} />
        <div className={styles.filterGroup}>
          <label>Balance:</label>
          <select value={balanceFilter} onChange={e => setBalanceFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="owed">Open Balance</option>
            <option value="clear">Fully Paid</option>
          </select>
        </div>
        <div className={styles.filterGroup}>
          <label>Nationality:</label>
          <select value={nationalityFilter} onChange={e => setNationalityFilter(e.target.value)}>
            <option value="all">All</option>
            {nationalities.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        {hasActiveFilters && (
          <button className={styles.clearFilters} onClick={handleClear}>Clear Filters</button>
        )}
      </div>

      {/* ── Results Summary ── */}
      <div className={styles.resultsSummary}>
        {loading ? (
          "Loading..."
        ) : (
          <>
            Showing {filtered.length} of {pagination?.total ?? customers.length} customers
            {hasActiveFilters && <span className={styles.filtered}>(filtered)</span>}
          </>
        )}
      </div>

      {/* ── Table ── */}
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Customer</th>
            <th>Contact</th>
            <th>Nationality</th>
            <th>Bookings</th>
            <th>Total Spent</th>
            <th>Open Balance</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="8">
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}>⏳</div>
                  <p className={styles.emptyTitle}>Loading customers...</p>
                </div>
              </td>
            </tr>
          ) : filtered.length > 0 ? filtered.map(c => (
            <tr key={c.id}>

              <td>
                <div className={styles.customerCell}>
                  <div className={styles.avatar} style={{ background: avatarColor(c.id) }}>
                    {initials(c)}
                  </div>
                  <div>
                    <div className={styles.customerName}>{c.firstName} {c.lastName}</div>
                    <div className={styles.customerRef}>{c.ref}</div>
                    <div className={styles.customerSince}>Since {c.createdAt}</div>
                  </div>
                </div>
              </td>

              <td>
                <div className={styles.contactEmail}>{c.email}</div>
                <div className={styles.contactPhone}>{c.phone}</div>
              </td>

              <td>
                <span className={styles.nationalityBadge}>{c.nationality}</span>
              </td>

              <td>
                {c.bookingsCount > 0
                  ? <span className={styles.bookingCount}>{c.bookingsCount} booking{c.bookingsCount !== 1 ? "s" : ""}</span>
                  : <span className={styles.noBookings}>No bookings</span>}
              </td>

              <td>
                <div className={styles.price}>
                  {c.totalSpent > 0
                    ? `€${c.totalSpent.toLocaleString("en-EU", { minimumFractionDigits: 2 })}`
                    : "—"}
                </div>
              </td>

              <td>
                {c.openBalance > 0
                  ? <span className={styles.balanceOwed}>€{c.openBalance.toLocaleString("en-EU", { minimumFractionDigits: 2 })}</span>
                  : <span className={styles.balanceClear}>✓ Clear</span>}
              </td>

              <td>
                <span className={`${styles.statusBadge} ${c.status === "Active" ? styles.statusActive : styles.statusInactive}`}>
                  <span className={styles.statusDot} />
                  {c.status}
                </span>
              </td>

              <td>
                <div className={styles.rowActions}>
                  <button className={styles.btnOpen}     onClick={() => handleOpenCustomer(c)}>Open</button>
                  <button className={styles.btnBookings} onClick={() => handleOpenBookings(c)}>Bookings</button>
                </div>
              </td>

            </tr>
          )) : (
            <tr>
              <td colSpan="8">
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}>👤</div>
                  <p className={styles.emptyTitle}>No customers found</p>
                  <p className={styles.emptySubtitle}>Try adjusting your filters or search term</p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ── Pagination ── */}
      {(pagination?.totalPages ?? 1) > 1 && (
        <div className={styles.pagination}>
          <span className={styles.paginationInfo}>
            Page {pagination?.page ?? page} of {pagination?.totalPages ?? 1} ({pagination?.total ?? 0} total)
          </span>
          <div className={styles.pageNumbers}>
            {pageNumbers.map((p, idx) =>
              p === "…" ? (
                <span key={`ellipsis-${idx}`} className={styles.ellipsis}>…</span>
              ) : (
                <button
                  key={p}
                  className={`${styles.pageBtn} ${Number(p) === Number(page) ? styles.pageBtnActive : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              )
            )}
          </div>
        </div>
      )}

      <AddPrivateCustomerModal
        open={modalOpen}
        onClose={() => { clearError?.(); setModalOpen(false); }}
        onSave={handleSave}
        customer={editingCustomer}
        apiError={createError}
      />

    </div>
  );
};

export default PrivateCustomers;